import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import DeliveryAssociateService from '../services/DeliveryAssociateService'
import Swal from 'sweetalert2'

const DeliveryAssociateList = () => {
    const [deliveryAssociates, setDeliveryAssociates] = useState([])
    const [loading, setLoading] = useState(true)
    const [pagination, setPagination] = useState({})
    const [search, setSearch] = useState('')
    const [sortBy, setSortBy] = useState('created_at')
    const [sortOrder, setSortOrder] = useState('desc')
    const [perPage, setPerPage] = useState(10)
    const [currentPage, setCurrentPage] = useState(1)

    // Modal states
    const [showModal, setShowModal] = useState(false)
    const [isEditing, setIsEditing] = useState(false)
    const [editingId, setEditingId] = useState(null)
    const [formData, setFormData] = useState({
        name: '',
        phone: ''
    })
    const [formErrors, setFormErrors] = useState({})
    const [submitting, setSubmitting] = useState(false)

    useEffect(() => {
        fetchDeliveryAssociates()
    }, [search, sortBy, sortOrder, perPage, currentPage])

    const fetchDeliveryAssociates = async () => {
        setLoading(true)
        try {
            const params = {
                search,
                sortBy,
                sortOrder,
                perPage,
                page: currentPage
            }
            const response = await DeliveryAssociateService.getDeliveryAssociates(params)
            if (response.success) {
                setDeliveryAssociates(response.data)
                setPagination(response.pagination)
            }
        } catch (error) {
            console.error('Error fetching delivery associates:', error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to fetch delivery associates',
                confirmButtonColor: '#d33'
            })
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = async (id, name) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: `You are about to delete "${name}". This action cannot be undone!`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        })

        if (result.isConfirmed) {
            try {
                const response = await DeliveryAssociateService.deleteDeliveryAssociate(id)
                if (response.success) {
                    Swal.fire({
                        icon: 'success',
                        title: 'Deleted!',
                        text: 'Delivery associate has been deleted.',
                        confirmButtonColor: '#3085d6',
                        timer: 2000,
                        showConfirmButton: false
                    })
                    fetchDeliveryAssociates()
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: response.errorMessage || 'Failed to delete delivery associate',
                        confirmButtonColor: '#d33'
                    })
                }
            } catch (error) {
                console.error('Error deleting delivery associate:', error)
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: error.errorMessage || 'Failed to delete delivery associate',
                    confirmButtonColor: '#d33'
                })
            }
        }
    }

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
        } else {
            setSortBy(field)
            setSortOrder('asc')
        }
        setCurrentPage(1)
    }

    const getSortIcon = (field) => {
        if (sortBy !== field) return 'fas fa-sort text-muted'
        return sortOrder === 'asc' ? 'fas fa-sort-up text-primary' : 'fas fa-sort-down text-primary'
    }

    // Modal functions
    const openCreateModal = () => {
        setIsEditing(false)
        setEditingId(null)
        setFormData({
            name: '',
            phone: ''
        })
        setFormErrors({})
        setShowModal(true)
    }

    const openEditModal = async (associate) => {
        setIsEditing(true)
        setEditingId(associate.id_delivery_associate)
        setFormData({
            name: associate.name || '',
            phone: associate.phone || ''
        })
        setFormErrors({})
        setShowModal(true)
    }

    const closeModal = () => {
        setShowModal(false)
        setIsEditing(false)
        setEditingId(null)
        setFormData({
            name: '',
            phone: ''
        })
        setFormErrors({})
        setSubmitting(false)
    }

    const handleInputChange = (e) => {
        const { name, value } = e.target
        setFormData(prev => ({
            ...prev,
            [name]: value
        }))
        // Clear error when user starts typing
        if (formErrors[name]) {
            setFormErrors(prev => ({
                ...prev,
                [name]: ''
            }))
        }
    }

    const validateForm = () => {
        const newErrors = {}

        if (!formData.name.trim()) {
            newErrors.name = 'Name is required'
        } else if (formData.name.trim().length < 2) {
            newErrors.name = 'Name must be at least 2 characters long'
        }

        if (!formData.phone.trim()) {
            newErrors.phone = 'Phone number is required'
        } else if (!/^\+?[\d\s\-\(\)]{10,}$/.test(formData.phone.trim())) {
            newErrors.phone = 'Please enter a valid phone number'
        }

        setFormErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        
        if (!validateForm()) return

        setSubmitting(true)
        try {
            const response = isEditing 
                ? await DeliveryAssociateService.updateDeliveryAssociate(editingId, formData)
                : await DeliveryAssociateService.createDeliveryAssociate(formData)

            if (response.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: `Delivery associate ${isEditing ? 'updated' : 'created'} successfully!`,
                    confirmButtonColor: '#3085d6',
                    timer: 2000,
                    showConfirmButton: false
                })
                closeModal()
                fetchDeliveryAssociates()
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: response.errorMessage || `Failed to ${isEditing ? 'update' : 'create'} delivery associate`,
                    confirmButtonColor: '#d33'
                })
            }
        } catch (error) {
            console.error('Error saving delivery associate:', error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.errorMessage || `Error ${isEditing ? 'updating' : 'creating'} delivery associate`,
                confirmButtonColor: '#d33'
            })
        } finally {
            setSubmitting(false)
        }
    }

    const renderPagination = () => {
        if (!pagination.last_page || pagination.last_page <= 1) return null

        const pages = []
        const maxVisiblePages = 5
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2))
        let endPage = Math.min(pagination.last_page, startPage + maxVisiblePages - 1)

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1)
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i)
        }

        return (
            <nav>
                <ul className="pagination justify-content-center">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => setCurrentPage(1)}
                            disabled={currentPage === 1}
                        >
                            First
                        </button>
                    </li>
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => setCurrentPage(currentPage - 1)}
                            disabled={currentPage === 1}
                        >
                            Previous
                        </button>
                    </li>
                    {pages.map(page => (
                        <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                            <button
                                className="page-link"
                                onClick={() => setCurrentPage(page)}
                            >
                                {page}
                            </button>
                        </li>
                    ))}
                    <li className={`page-item ${currentPage === pagination.last_page ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => setCurrentPage(currentPage + 1)}
                            disabled={currentPage === pagination.last_page}
                        >
                            Next
                        </button>
                    </li>
                    <li className={`page-item ${currentPage === pagination.last_page ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => setCurrentPage(pagination.last_page)}
                            disabled={currentPage === pagination.last_page}
                        >
                            Last
                        </button>
                    </li>
                </ul>
            </nav>
        )
    }

    return (
        <>
            <div className="container-fluid">
                {/* Page Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h1 className="h3 mb-0">Delivery Associates</h1>
                        <p className="text-muted mb-0">Manage delivery personnel</p>
                    </div>
                    <button onClick={openCreateModal} className="btn btn-primary">
                        <i className="fas fa-plus me-2"></i>Add New Associate
                    </button>
                </div>

                {/* Filters */}
                <div className="card mb-4">
                    <div className="card-body">
                        <div className="row g-3">
                            <div className="col-md-4">
                                <label className="form-label">Search</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search by name or phone..."
                                    value={search}
                                    onChange={(e) => {
                                        setSearch(e.target.value)
                                        setCurrentPage(1)
                                    }}
                                />
                            </div>
                            <div className="col-md-2">
                                <label className="form-label">Per Page</label>
                                <select
                                    className="form-select"
                                    value={perPage}
                                    onChange={(e) => {
                                        setPerPage(parseInt(e.target.value))
                                        setCurrentPage(1)
                                    }}
                                >
                                    <option value="10">10</option>
                                    <option value="25">25</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </select>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label">Actions</label>
                                <div>
                                    <button
                                        className="btn btn-outline-secondary"
                                        onClick={fetchDeliveryAssociates}
                                    >
                                        <i className="fas fa-sync-alt me-2"></i>Refresh
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Data Table */}
                <div className="card">
                    <div className="card-header">
                        <h5 className="card-title mb-0">
                            Delivery Associates
                            {pagination.total && (
                                <span className="text-muted ms-2">({pagination.total} total)</span>
                            )}
                        </h5>
                    </div>
                    <div className="card-body p-0">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-2">Loading delivery associates...</p>
                            </div>
                        ) : deliveryAssociates.length > 0 ? (
                            <>
                                <div className="table-responsive">
                                    <table className="table table-hover mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th onClick={() => handleSort('id_delivery_associate')} style={{ cursor: 'pointer' }}>
                                                    ID <i className={getSortIcon('id_delivery_associate')}></i>
                                                </th>
                                                <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                                                    Name <i className={getSortIcon('name')}></i>
                                                </th>
                                                <th onClick={() => handleSort('phone')} style={{ cursor: 'pointer' }}>
                                                    Phone <i className={getSortIcon('phone')}></i>
                                                </th>
                                                <th onClick={() => handleSort('created_at')} style={{ cursor: 'pointer' }}>
                                                    Created <i className={getSortIcon('created_at')}></i>
                                                </th>
                                                <th width="150">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {deliveryAssociates.map((associate) => (
                                                <tr key={associate.id_delivery_associate}>
                                                    <td>
                                                        <span className="fw-bold">#{associate.id_delivery_associate}</span>
                                                    </td>
                                                    <td>
                                                        <div>
                                                            <span className="fw-bold">{associate.name}</span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className="text-muted">{DeliveryAssociateService.formatPhoneNumber(associate.phone)}</span>
                                                    </td>
                                                    <td>
                                                        <span className="text-muted">{DeliveryAssociateService.formatDate(associate.created_at)}</span>
                                                    </td>
                                                    <td>
                                                        <div className="btn-group" role="group">
                                                            <Link
                                                                to={`/delivery-associates/${associate.id_delivery_associate}`}
                                                                className="btn btn-outline-primary btn-sm"
                                                                title="View Details"
                                                            >
                                                                <i className="fas fa-eye"></i>
                                                            </Link>
                                                            <button
                                                                className="btn btn-outline-secondary btn-sm"
                                                                title="Edit"
                                                                onClick={() => openEditModal(associate)}
                                                            >
                                                                <i className="fas fa-edit"></i>
                                                            </button>
                                                            <button
                                                                className="btn btn-outline-danger btn-sm"
                                                                title="Delete"
                                                                onClick={() => handleDelete(associate.id_delivery_associate, associate.name)}
                                                            >
                                                                <i className="fas fa-trash"></i>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                
                                {/* Pagination */}
                                <div className="card-footer">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div className="text-muted">
                                            Showing {pagination.from || 0} to {pagination.to || 0} of {pagination.total || 0} entries
                                        </div>
                                        {renderPagination()}
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="text-center py-5">
                                <div className="text-muted">
                                    <i className="fas fa-users fa-3x mb-3"></i>
                                    <p>No delivery associates found</p>
                                    {search && (
                                        <button
                                            className="btn btn-outline-primary"
                                            onClick={() => {
                                                setSearch('')
                                                setCurrentPage(1)
                                            }}
                                        >
                                            Clear Search
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Create/Edit Modal */}
            {showModal && (
                <div className='modal show modal-adaptive' style={{display: 'block', backgroundColor: 'rgba(0,0,0,0.6)'}} onClick={closeModal}>
                    <div className='modal-dialog modal-dialog-adaptive' onClick={(e) => e.stopPropagation()}>
                        <div className='modal-content delivery-associate-modal'>
                            <div className='modal-header border-bottom sticky-top bg-white'>
                                <h4 className='modal-title mb-0'>
                                    <i className='fas fa-truck text-primary me-2'></i>
                                    {isEditing ? 'Edit Delivery Associate' : 'Add New Delivery Associate'}
                                </h4>
                                <button type='button' className='btn-close' onClick={closeModal} disabled={submitting}></button>
                            </div>
                            <div className='modal-body modal-body-scrollable p-4'>
                                <form onSubmit={handleSubmit}>
                                    <div className='row g-3'>
                                        {/* Name */}
                                        <div className='col-12'>
                                            <div className='form-group-mui'>
                                                <input
                                                    type='text'
                                                    className={`form-control form-control-mui ${formErrors.name ? 'is-invalid' : ''}`}
                                                    id='name'
                                                    name='name'
                                                    value={formData.name}
                                                    onChange={handleInputChange}
                                                    placeholder=' '
                                                    required
                                                    disabled={submitting}
                                                />
                                                <label htmlFor='name' className='form-label-mui'>
                                                    <i className='fas fa-user text-muted me-2'></i>
                                                    Associate Name *
                                                </label>
                                                {formErrors.name && (
                                                    <div className="invalid-feedback d-block">
                                                        {formErrors.name}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Phone */}
                                        <div className='col-12'>
                                            <div className='form-group-mui'>
                                                <input
                                                    type='tel'
                                                    className={`form-control form-control-mui ${formErrors.phone ? 'is-invalid' : ''}`}
                                                    id='phone'
                                                    name='phone'
                                                    value={formData.phone}
                                                    onChange={handleInputChange}
                                                    placeholder=' '
                                                    required
                                                    disabled={submitting}
                                                />
                                                <label htmlFor='phone' className='form-label-mui'>
                                                    <i className='fas fa-phone text-muted me-2'></i>
                                                    Phone Number *
                                                </label>
                                                {formErrors.phone && (
                                                    <div className="invalid-feedback d-block">
                                                        {formErrors.phone}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Guidelines */}
                                      
                                    </div>
                                </form>
                            </div>
                            <div className='modal-footer delivery-associate-modal-footer bg-white border-top sticky-bottom'>
                                <button type='button' className='btn btn-outline-secondary' onClick={closeModal} disabled={submitting}>
                                    <i className='fas fa-times me-2'></i>
                                    Cancel
                                </button>
                                <button type='button' className='btn btn-primary btn-modern' onClick={handleSubmit} disabled={submitting}>
                                    {submitting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                            {isEditing ? 'Updating...' : 'Creating...'}
                                        </>
                                    ) : (
                                        <>
                                            <i className={`fas ${isEditing ? 'fa-save' : 'fa-plus'} me-2`}></i>
                                            {isEditing ? 'Update Associate' : 'Create Associate'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <style jsx>{`
                /* Modal Styles - Enhanced Modal Styles similar to CustomerList */
                .modal-adaptive {
                    padding: 0;
                }
                
                .modal-dialog-adaptive {
                    margin: 0;
                    display: flex;
                    flex-direction: column;
                }
                
                .modal-content {
                    display: flex;
                    flex-direction: column;
                    border: 0;
                }
                
                .sticky-top {
                    position: sticky;
                    top: 0;
                    z-index: 1020;
                }
                
                .sticky-bottom {
                    position: sticky;
                    bottom: 0;
                    z-index: 1020;
                }
                
                .modal-body-scrollable {
                    flex: 1;
                    overflow-y: auto;
                    padding-bottom: 2rem;
                }

                /* Mobile specific styles - Full screen */
                @media (max-width: 768px) {
                    .modal-adaptive {
                        padding: 0;
                    }
                    
                    .modal-dialog-adaptive {
                        width: 100vw;
                        height: 100vh;
                        margin: 0;
                        max-width: none;
                    }
                    
                    .modal-content {
                        height: 100vh;
                        border-radius: 0;
                    }
                    
                    .modal-body-scrollable {
                        padding: 1rem !important;
                        padding-bottom: 2rem !important;
                    }
                    
                    .modal-header {
                        padding: 0.75rem 1rem;
                    }
                    
                    .modal-footer {
                        padding: 0.75rem 1rem;
                        flex-wrap: wrap;
                        gap: 0.5rem;
                    }
                    
                    .modal-footer .btn {
                        flex: 1;
                        min-width: auto;
                    }
                }
                
                /* Desktop styles - Medium modal */
                @media (min-width: 769px) {
                    .modal-adaptive {
                        padding: 0;
                        display: flex !important;
                        align-items: center !important;
                        justify-content: center !important;
                        min-height: 100vh;
                    }
                    
                    .modal-dialog-adaptive {
                        max-width: 600px;
                        width: 85%;
                        margin: auto;
                        height: auto;
                        max-height: 85vh;
                        position: relative;
                        transform: none;
                        top: auto;
                        left: auto;
                    }
                    
                    .modal-content {
                        border-radius: 0.5rem;
                        box-shadow: 0 0.5rem 2rem rgba(0, 0, 0, 0.15);
                        height: auto;
                        max-height: 85vh;
                        display: flex;
                        flex-direction: column;
                    }
                    
                    .modal-body-scrollable {
                        padding: 1.5rem !important;
                        max-height: calc(85vh - 120px);
                        flex: 1;
                        overflow-y: auto;
                    }
                    
                    .modal-header {
                        border-radius: 0.5rem 0.5rem 0 0;
                        padding: 1rem 1.5rem;
                        flex-shrink: 0;
                    }
                    
                    .modal-footer {
                        border-radius: 0 0 0.5rem 0.5rem;
                        padding: 1rem 1.5rem;
                        flex-shrink: 0;
                    }
                }

                /* Large desktop screens */
                @media (min-width: 1200px) {
                    .modal-dialog-adaptive {
                        max-width: 700px;
                        width: 60%;
                    }
                }

                .delivery-associate-modal .modal-header {
                    background: white;
                    color: #374151;
                    border-radius: 12px 12px 0 0;
                    border-bottom: 1px solid #e5e7eb;
                    padding: 1.5rem 2rem;
                }

                .delivery-associate-modal .modal-header .modal-title {
                    font-weight: 600;
                    font-size: 1.2rem;
                }

                .delivery-associate-modal .modal-header .btn-close {
                    filter: none;
                    opacity: 0.6;
                }

                .delivery-associate-modal .modal-header .btn-close:hover {
                    opacity: 1;
                }

                .delivery-associate-modal .modal-body {
                    background-color: white;
                    padding: 2rem;
                }

                /* Material-UI Style Floating Labels */
                .form-group-mui {
                    position: relative;
                    margin-bottom: 1.5rem;
                    width: 100%;
                }

                .form-control-mui {
                    border: 1px solid #d1d5db;
                    border-radius: 6px;
                    padding: 1rem 1rem 0.5rem 1rem;
                    font-size: 14px;
                    transition: all 0.2s ease;
                    background-color: white;
                    position: relative;
                    z-index: 1;
                    width: 100%;
                    box-sizing: border-box;
                    outline: none;
                    -webkit-font-smoothing: antialiased;
                    -moz-osx-font-smoothing: grayscale;
                    text-rendering: optimizeLegibility;
                    color: #374151;
                    line-height: 1.5;
                }

                .form-control-mui:focus {
                    border-color: #3b82f6;
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
                    background-color: white;
                    outline: none;
                }

                .form-control-mui:hover {
                    border-color: #9ca3af;
                }

                .form-control-mui.is-invalid {
                    border-color: #dc3545;
                }

                .form-control-mui.is-invalid:focus {
                    border-color: #dc3545;
                    box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.25);
                }

                .form-label-mui {
                    position: absolute;
                    top: 50%;
                    left: 1rem;
                    transform: translateY(-50%);
                    color: #6b7280;
                    font-size: 14px;
                    font-weight: 400;
                    pointer-events: none;
                    transition: all 0.2s ease;
                    background-color: white;
                    padding: 0 0.25rem;
                    z-index: 2;
                    white-space: nowrap;
                    -webkit-font-smoothing: antialiased;
                    -moz-osx-font-smoothing: grayscale;
                    text-rendering: optimizeLegibility;
                }

                /* Floating label animation */
                .form-control-mui:focus ~ .form-label-mui,
                .form-control-mui:not(:placeholder-shown) ~ .form-label-mui {
                    top: -0.5rem;
                    left: 0.75rem;
                    transform: scale(0.8);
                    color: #3b82f6;
                    font-weight: 500;
                    background-color: white;
                    padding: 0 0.5rem;
                }

                .form-control-mui:valid ~ .form-label-mui,
                .form-control-mui[value]:not([value=""]) ~ .form-label-mui {
                    top: -0.5rem;
                    left: 0.75rem;
                    transform: scale(0.8);
                    color: #3b82f6;
                    font-weight: 500;
                    background-color: white;
                    padding: 0 0.5rem;
                }

                .delivery-associate-modal-footer {
                    background-color: white;
                    border-top: 1px solid #e9ecef;
                    border-radius: 0 0 12px 12px;
                    padding: 1.5rem 2rem;
                    display: flex;
                    justify-content: flex-end;
                    gap: 1rem;
                }

                .btn-modern {
                    border-radius: 8px;
                    padding: 0.625rem 1.5rem;
                    font-weight: 500;
                    font-size: 14px;
                    transition: all 0.3s ease;
                    border: none;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .btn-modern:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                }

                .btn-modern:active {
                    transform: translateY(0);
                }

                .btn-outline-secondary {
                    border: 2px solid #6c757d;
                    color: #6c757d;
                    background-color: transparent;
                    border-radius: 8px;
                    padding: 0.625rem 1.5rem;
                    font-weight: 500;
                    font-size: 14px;
                    transition: all 0.3s ease;
                }

                .btn-outline-secondary:hover {
                    background-color: #6c757d;
                    color: white;
                    transform: translateY(-1px);
                    box-shadow: 0 4px 12px rgba(108, 117, 125, 0.3);
                }

                .invalid-feedback {
                    color: #dc3545;
                    font-size: 12px;
                    margin-top: 0.25rem;
                }

                /* Enhanced Mobile Modal Adjustments */
                @media (max-width: 768px) {
                    .delivery-associate-modal .modal-body {
                        padding: 1rem 1.5rem;
                        padding-top: 5.5rem;
                        padding-bottom: 5.5rem;
                    }

                    .delivery-associate-modal-footer {
                        padding: 1rem 1.5rem;
                        flex-direction: row;
                        gap: 0.75rem;
                        justify-content: stretch;
                    }

                    .delivery-associate-modal-footer .btn {
                        flex: 1;
                        margin-bottom: 0;
                        min-height: 2.75rem;
                        font-size: 0.9rem;
                        font-weight: 600;
                    }

                    .form-label-mui {
                        font-size: 13px;
                    }

                    .form-control-mui {
                        font-size: 13px;
                        min-height: 2.5rem;
                    }

                    /* Modal title adjustments for mobile */
                    .modal-title {
                        font-size: 1.1rem;
                        font-weight: 600;
                        line-height: 1.3;
                    }

                    /* Close button mobile adjustments */
                    .btn-close {
                        padding: 0.5rem;
                        font-size: 1.2rem;
                    }
                }

                /* Extra small mobile adjustments */
                @media (max-width: 576px) {
                    .delivery-associate-modal .modal-body {
                        padding: 0.75rem 1rem;
                        padding-top: 5.5rem;
                        padding-bottom: 5.5rem;
                    }

                    .delivery-associate-modal-footer {
                        padding: 0.75rem 1rem;
                        flex-direction: column;
                        gap: 0.5rem;
                    }

                    .delivery-associate-modal-footer .btn {
                        width: 100%;
                        min-height: 2.5rem;
                        font-size: 0.875rem;
                    }

                    .modal-title {
                        font-size: 1rem;
                    }

                    .form-control-mui {
                        font-size: 12px;
                        min-height: 2.25rem;
                    }

                    .form-label-mui {
                        font-size: 12px;
                    }
                }

                /* Icons styling */
                .text-muted {
                    opacity: 0.6;
                }

                .form-label-mui i {
                    margin-right: 0.5rem;
                }

                /* Focus and hover effects */
                .form-group-mui:hover .form-label-mui {
                    color: #3D9BD6;
                }

                .form-control-mui:focus + .form-label-mui i,
                .form-group-mui:hover .form-label-mui i {
                    color: #3D9BD6;
                }
            `}</style>
        </>
    )
}

export default DeliveryAssociateList 